import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { ResizeHandleState } from '@helios/generic-components'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import {
  RootState,
  selectActiveSession,
  selectFirstLoadedAccountId,
  selectFlexExternalPositionsSessionCacheStatus,
  selectFlexPositionsSessionCacheStatus,
  selectIsEnfusionSession,
  SessionCacheStatus,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import {
  EventType,
  FlexPositionRowUpdateEvent,
  FlexPositionRowUpdatePayload,
  flexPositionRowIds,
  HeliosMessageType,
  SessionMessagingService,
} from '@helios/messaging'
import { ComponentContainer } from 'golden-layout'
import { combineLatest, Subject } from 'rxjs'
import { distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { GridRole, SourceKey } from './models/source-key.model'
import { WorkbenchStoreService } from './services/workbench-store.service'
import { FieldCatalogService } from './services/field-catalog.service'
import { HttpFieldCatalogService } from './services/http-field-catalog.service'
import { MatchingService } from './services/matching.service'
import { AggregateInfoService } from './services/aggregate-info.service'
import { WorkbenchDataSource } from './services/workbench-data-source'
import { HttpWorkbenchDataSource } from './services/http-workbench-data-source'
import { FlexPositionComparisonService } from './services/flex-position-comparison.service'
import { HttpFlexPositionComparisonService } from './services/http-flex-position-comparison.service'
import { RowDetailService } from './services/row-detail.service'
import { HttpRowDetailService } from './services/http-row-detail.service'
import { MatchRefreshService } from './services/match-refresh.service'
import {
  POSITION_ACTIONS_ENDPOINT_AVAILABLE,
  PositionsTaskService,
} from './services/positions-task.service'
import { HttpPositionsTaskService } from './services/http-positions-task.service'
import { StubPositionsTaskService } from './services/stub-positions-task.service'
import { PositionsExportService } from './services/positions-export.service'
import { HttpPositionsExportService } from './services/http-positions-export.service'
import {
  isSubPanelTabDisabled,
  resolveSubPanelTab,
  SUB_PANEL_TABS,
  SubPanelTabKey,
} from './sub-panels/sub-panel-tab.model'

/**
 * Persisted panel state. Every field is optional because a freshly opened panel has none of it yet,
 * and because `BasePanelComponent.setState` replaces the whole object — each writer merges over the
 * current state rather than assuming the others have already run.
 */
interface Positions2PanelState {
  /** Splitter between the Comparison and Base grids. */
  gridResizeHandleState?: ResizeHandleState
  /** Splitter between the Base grid and the sub-panel tab strip. */
  detailResizeHandleState?: ResizeHandleState
  activeTab?: SubPanelTabKey
}

/**
 * Gate `<hs-cache-load-listener>` on a fully-loaded session cache, by reporting PARTIAL as STARTED.
 *
 * The shared listener releases its content on PARTIAL, DONE or POST_LOAD_DONE. That is right for a
 * single-grid panel: showing rows as they arrive beats showing a spinner. It is wrong for THIS panel,
 * which reconciles two independently-loaded grids against each other. A grid that fetches while its
 * cache is only PARTIAL publishes an incomplete row set into the shared match index, and the sibling
 * grid then renders confident MATCHED/UNMATCHED verdicts and match counts computed against rows that
 * simply had not loaded yet — a wrong answer that looks exactly like a right one. Worse, it does not
 * self-heal: the grid is created once and never re-fetches on the later DONE, so the bad verdicts
 * persist until an account or source change happens to re-trigger it.
 *
 * Reporting PARTIAL as STARTED makes the listener treat it as "still loading": spinner stays, content
 * is not created, no request is issued. DONE / POST_LOAD_DONE still release it, ERROR still shows the
 * error card, STARTED / UNKNOWN still show the spinner — so this is strictly a narrowing of when the
 * grids mount, not a new state.
 *
 * CROSS-REPO INVARIANT — this is deliberately done HERE, in the one panel that needs it, rather than
 * in `CacheLoadListenerComponent`. That component is shared with Positions 1.0, external-transactions,
 * clearwater-transactions and transactions-2, none of which reconcile two grids against a shared
 * index; changing its PARTIAL handling would make every one of them slower to first paint to fix a
 * problem only this panel has. transactions-2 documents accepting the 1.0 behaviour for exactly that
 * reason (see `transactions-2-panel.component.ts`). Keep the shared listener untouched: if a second
 * panel ever needs this gate, lift THIS function into a shared util, not the behaviour into the
 * listener.
 */
const gateOnPartial = (status: SessionCacheStatus): SessionCacheStatus =>
  status === SessionCacheStatus.PARTIAL ? SessionCacheStatus.STARTED : status

/**
 * Composite host for Positions 2.0. Stacks a Comparison grid over a Base grid over the detail
 * sub-panel tabs, with a draggable splitter between each. The two grids are coordinated entirely
 * through the panel-scoped {@link WorkbenchStoreService} (source assignment, selection sync,
 * cross-filter, related-row highlighting) — they never reference each other directly.
 *
 * The existing Positions (1.0) panel is untouched; 2.0 ships side by side as a separate panel.
 */
@Component({
  selector: 'hs-positions-2',
  templateUrl: './positions-2-panel.component.html',
  styleUrls: ['./positions-2-panel.component.scss'],
  providers: [
    WorkbenchStoreService,
    MatchingService,
    AggregateInfoService,
    // PVAL-26237: rows come live from the proxied flex-positions query API, per source, once that
    // grid's column catalog has resolved (HttpWorkbenchDataSource needs the field list).
    { provide: WorkbenchDataSource, useClass: HttpWorkbenchDataSource },
    // Columns come live from the proxied flex-positions field-catalog API.
    { provide: FieldCatalogService, useClass: HttpFieldCatalogService },
    // PVAL-26240 is deployed — the Position Comparison sub-panel hits the real backend.
    {
      provide: FlexPositionComparisonService,
      useClass: HttpFlexPositionComparisonService,
    },
    // PVAL-26156 is deployed — the Position Detail sub-panel hits the real row-detail API. Bound
    // here rather than `providedIn: 'root'` so its (source, rowId) cache is scoped to this panel
    // instance and its account session.
    { provide: RowDetailService, useClass: HttpRowDetailService },
    // PVAL-26403: every context-menu matching action dispatches through here, and now resolves to
    // the real `POST /flex-positions/actions` — see POSITION_ACTIONS_ENDPOINT_AVAILABLE in
    // positions-task.service.ts, which is the whole switch and carries the deploy caveat (helios
    // !487 / !491 define the endpoint and were unmerged when this was flipped).
    //
    // This is the ONE dispatch path. PVAL-26416 arrived with a parallel implementation of the same
    // POST (FlexPositionActionsService / PositionsActionsTaskService); it was dropped rather than
    // bound alongside, because two services posting to one endpoint is not a configuration anyone
    // can reason about. What it contributed instead is the read-back half below, plus the two
    // transport details this service was missing — see HttpPositionsTaskService.
    {
      provide: PositionsTaskService,
      useClass: POSITION_ACTIONS_ENDPOINT_AVAILABLE
        ? HttpPositionsTaskService
        : StubPositionsTaskService,
    },
    // PVAL-26416: the read-back half — re-reads and patches exactly the rows a flex position
    // update event names, so a match/unmatch shows up without a Run or a full refetch.
    MatchRefreshService,
    // PVAL-26421: "Export to Excel" renders server-side through POST /flex-positions/export
    // (helios !497). Until that is deployed every export ends on the grid's error toast.
    { provide: PositionsExportService, useClass: HttpPositionsExportService },
  ],
})
export class Positions2PanelComponent
  extends BasePanelComponent<Positions2PanelState>
  implements OnInit, OnDestroy
{
  /**
   * Which grid source each update event drives a refresh of.
   *
   * The event type is the ONLY thing that can name a feed here: a position rowId does not identify
   * its own source, and ENFUSION and PROMETHEUS can share one. So both events stay separately
   * subscribed, each refreshing the one source it is paired with.
   *
   * What the pairing does NOT mean — and the backend is explicit about this
   * (`createPositionRowChangedEvent`, helios !487) — is that each event carries only its own feed's
   * ids. `rowIds` is the SAME unfiltered cross-feed union in both. That is harmless here because
   * `loadByIds` is scoped by source and the backend treats an id it does not hold as a no-op, so
   * each refresh naturally keeps only the rows that are its own. It matters only if someone later
   * "optimises" by filtering one event's payload down to one feed, or by dropping one subscription
   * as redundant — the first would be a no-op and the second would stop refreshing that grid.
   */
  private static readonly UPDATE_EVENT_SOURCE: ReadonlyArray<
    [HeliosMessageType, SourceKey]
  > = [
    [HeliosMessageType.FlexPositionUpdate, 'api'],
    [HeliosMessageType.FlexExternalPositionUpdate, 'prom'],
  ]

  readonly comparisonRole: GridRole = 'comparison'
  readonly baseRole: GridRole = 'base'
  readonly tabs = SUB_PANEL_TABS

  /**
   * The initial vertical split, as flex-grow values bound inline on each region.
   *
   * Deliberately not in the stylesheet: hs-resize-handle reads the starting flex-grow off the
   * element's **inline** style and assumes 1 when it is absent, so a CSS-only grow (or a flex-basis)
   * makes it rewrite the ratio on the first drag and jump. Bound once — Angular does not rewrite a
   * style binding whose value has not changed, so the handle's own writes, and any restored
   * `ResizeHandleState`, both survive.
   */
  readonly regionGrow = { comparison: '2', base: '2', detail: '1' }

  /**
   * Whether the active session is Enfusion-sourced. Positions 2.0 has nothing to show for a CW
   * session — no fetch was ever going to succeed against it — so the template gates the whole
   * workbench on this rather than rendering an empty grid shell (headers, toolbar, "No Data") for a
   * session this panel fundamentally does not apply to.
   */
  isEnfusionSession = false

  /**
   * Session-cache status behind each grid's fixed source — Base always reads Enfusion (Flex
   * Positions), Comparison always reads Prometheus (Flex External Positions), per the registry's
   * current `disabledRoles` (see `source-registry.ts`). This binding is actually enforced here:
   * `WorkbenchStoreService.setSource` checks `isSourceSelectable`, the same rule the picker uses.
   * The transactions-2 twin does not enforce the equivalent — see the note on `baseCacheStatus` in
   * `transactions-2-panel.component.ts`. Fed into `<hs-cache-load-listener>` per grid in the
   * template, same mechanism Positions 1.0 uses: the grid is created once its status releases the
   * listener, so nothing inside `WorkbenchGridComponent` needs to gate its own fetch.
   *
   * These hold the GATED status, not the raw one — see {@link gateOnPartial}. Positions 2.0 does
   * NOT release its grids on Partial the way 1.0 does.
   */
  baseCacheStatus = SessionCacheStatus.UNKNOWN
  comparisonCacheStatus = SessionCacheStatus.UNKNOWN

  /** Diagnostics `<hs-cache-load-listener>` surfaces on its "Cache Load Error" card, matching what
   *  every 1.0 panel that wraps in it already passes. */
  currentSessionId?: string
  currentAccountIds?: number[]

  private readonly destroy$ = new Subject<void>()

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly store: WorkbenchStoreService,
    private readonly fieldCatalog: FieldCatalogService,
    @Inject(RowDetailService) private readonly rowDetail: RowDetailService,
    private readonly rootStore: Store<RootState>,
    private readonly messagingService: SessionMessagingService,
    private readonly matchRefresh: MatchRefreshService,
  ) {
    super(container)
  }

  /**
   * The tab to show. A persisted key for a disabled tab (a layout saved on Historical Balance before
   * it was disabled) falls back to the default; the saved value is not rewritten, so the layout
   * reopens on that tab once it is re-enabled.
   */
  get activeTab(): SubPanelTabKey {
    return resolveSubPanelTab(this.state?.activeTab)
  }

  ngOnInit(): void {
    // The account comes from the active session, not from the Accounts panel. Selecting a different
    // account while a session is open leaves this alone; only creating or switching a session moves
    // it, which is what keeps the field-catalog fetch behind session creation.
    //
    // Flex Positions has no data for a CW session — the backend gates the read/readiness endpoints
    // to SessionKind.ENFN — so this panel must not focus an account loaded into one: focusing it
    // would either 400 against the catalog or leave the grids waiting on a readiness signal the
    // backend will never emit. Passing null instead reuses the existing no-account-focused path
    // (empty computed columns, no fetch, no mask) rather than adding a new state for it.
    combineLatest([
      this.rootStore.select(selectFirstLoadedAccountId),
      this.rootStore.select(selectIsEnfusionSession),
    ])
      .pipe(
        // A session update that leaves its accounts alone (commands, recon date, pin) must not
        // re-trigger the column pipeline.
        distinctUntilChanged(([a, ea], [b, eb]) => a === b && ea === eb),
        takeUntil(this.destroy$),
      )
      .subscribe(([accountId, isEnfusionSession]) => {
        this.isEnfusionSession = isEnfusionSession
        this.onAccountIdChange(isEnfusionSession ? accountId ?? null : null)
      })

    this.rootStore
      .select(selectFlexPositionsSessionCacheStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => (this.baseCacheStatus = gateOnPartial(status)))

    this.rootStore
      .select(selectFlexExternalPositionsSessionCacheStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => (this.comparisonCacheStatus = gateOnPartial(status)))

    this.rootStore
      .select(selectActiveSession)
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.currentSessionId = session?.id
        this.currentAccountIds = session?.loadedAccountIds
      })

    this.subscribeToMatchUpdates()
  }

  /**
   * The panel's first messaging subscription. Websocket event filters are registered once app-wide
   * (helios-messaging.service.ts's SESSION_ID_FILTER_KEY), so a panel only calls `on()`.
   *
   * Every subscription is torn down with the panel: an update event delivered after destroy must
   * issue no by-ids request and touch no grid.
   */
  private subscribeToMatchUpdates(): void {
    Positions2PanelComponent.UPDATE_EVENT_SOURCE.forEach(
      ([eventType, source]) => {
        this.messagingService
          .on<FlexPositionRowUpdatePayload, FlexPositionRowUpdateEvent>(
            EventType.MESSAGE,
            [eventType],
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe(event => {
            const rowIds = flexPositionRowIds(event?.payload)
            if (!rowIds.length) return
            this.matchRefresh
              .refresh(source, rowIds)
              .pipe(takeUntil(this.destroy$))
              .subscribe()
          })
      },
    )
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  onTabChange(key: SubPanelTabKey): void {
    // ngbNav already refuses to activate a disabled item; this guards any other caller.
    if (isSubPanelTabDisabled(key)) return
    this.mergeState({ activeTab: key })
  }

  /**
   * The account loaded into the active session. Enfusion columns resolve per CW client, so nothing
   * is cached across accounts: invalidate the previous account's catalogs before pushing the new id
   * (the column pipeline then refetches).
   *
   * Row detail is invalidated wholesale rather than per account: its cache is keyed on
   * (source, rowId) only, and a row id belongs to exactly one account, so an account switch retires
   * every key it holds.
   */
  onAccountIdChange(accountId: number | null): void {
    const previous = this.store.accountIdValue()
    if (previous != null) this.fieldCatalog.invalidate(previous)
    this.rowDetail.invalidate()
    this.store.setAccountId(accountId)
  }

  onGridResizeFinished(gridResizeHandleState: ResizeHandleState): void {
    this.mergeState({ gridResizeHandleState })
  }

  onDetailResizeFinished(detailResizeHandleState: ResizeHandleState): void {
    this.mergeState({ detailResizeHandleState })
  }

  /** `setState` replaces the state object, so every write has to carry the rest of it forward. */
  private mergeState(patch: Positions2PanelState): void {
    this.setState({ ...this.state, ...patch })
  }
}
